import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import type { SxProps } from "@mui/material/styles";
import {
  type BillingProduct,
  PRODUCT_DISPLAY_NAMES,
} from "@polifonia/interlude/billingConstants";
import type { FeatureLimit } from "@polifonia/interlude/models";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { useActiveBillingFeatures } from "@/features/billing/useActiveBillingFeatures";
import { FeatureUsageRow } from "@/pages/billing/v2/tabs/overview/FeatureUsageRow";

const styles = {
  card: {
    border: `1px solid ${theme.colors.neutral020}`,
    borderRadius: "12px",
  },
  cardContent: {
    padding: "24px",
    "&:last-child": {
      paddingBottom: "24px",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ProductUsageCardProps {
  product: BillingProduct;
  features: Array<FeatureLimit>;
}

export const ProductUsageCard: React.FC<ProductUsageCardProps> =
  function ProductUsageCard({ product, features }) {
    const { t } = useTranslation();
    const activeBillingFeatures = useActiveBillingFeatures();

    return (
      <Card variant="outlined" sx={styles.card}>
        <CardContent sx={styles.cardContent}>
          <Box sx={styles.header}>
            <Typography variant="h2">
              {t("billing.productUsage.title", {
                productName: PRODUCT_DISPLAY_NAMES[product],
              })}
            </Typography>
          </Box>
          <Box>
            {features.map((feature) => (
              <FeatureUsageRow
                key={feature.feature}
                feature={feature}
                isActive={activeBillingFeatures.has(feature.feature)}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };
